import { Component, Inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { AgGridLangMap, IoniconsOutline, PtEntity, PtUiAppRoutes } from '@pt/pt-models';
import { ItemDocDependency, WorkspaceItemType } from '@primatoo-ms-organization';
import { ColDef, GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { WidgetConfig } from '../../../../pt-core/src/lib/widget-config';
import { PtCoreActions } from '@pt/pt-core';
import { Store } from '@ngrx/store';
import { CustomRendererComponent } from '../ag-grid';
import { DocumentItemUtils } from '../../../../pt-core/src/lib/utils/document-item.utils';
import { DeleteEntityServiceInterface } from '@pt/api-client';

@Component({
  selector: 'pt-confirm-delete-data-protection-governance-item',
  template: `
    <div class='modal-header'
         (click)='$event.stopPropagation()'>
      <h4 class='modal-title'>
        {{ title | async }}
      </h4>
    </div>

    <div class='modal-body' (click)='$event.stopPropagation()'>
      <p [innerHTML]='message | async'></p>

      <div *ngIf='rowData?.length > 0'>
        <label class="form-label" *ngIf="entityType !== 'globalMeasureCheck'" [innerHTML]="'MESSAGE.DEPENDENCIES_DELETE_V2' | translate"></label>
        <label class="form-label" *ngIf="entityType === 'globalMeasureCheck'" [innerHTML]="'MESSAGE.DEPENDENCIES_DELETE_V2_GLOBAL_MEASURE_CHECK' | translate"></label>
        <ag-grid-angular
          *ngIf='rowData?.length > 0'
          style='width: 100%; height: 325px;'
          class='ag-theme-balham'
          [rowData]='rowData'
          [columnDefs]='columnDefs'
          [gridOptions]='gridOptions'>
        </ag-grid-angular>
      </div>
    </div>

    <div class='modal-footer'
         (click)='$event.stopPropagation()'>
      <button type='button' class='btn btn-danger btn-sm'
              (click)='activeModal.close(true); $event.stopPropagation()'>
        {{ 'BUTTON.DELETE' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(false); $event.stopPropagation()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>

    <ng-template #actionCol let-node>
      <pt-icon class='cursor-pointer'
               [key]='IconOpen'
               (click)='selectItem(node.row)'
      ></pt-icon>
    </ng-template>


    <ng-template #nameCol let-node>
      <pt-image-renderer
        [showIcon]="true"
        [label]='node.row.name'
        [tooltip]="'LABELS.'+ getResourceType(node.row) | translate"
        [type]='getResourceType(node.row)'
      ></pt-image-renderer>
    </ng-template>
  `
})
export class ConfirmDeleteDataProtectionGovernanceItemDialogComponent implements OnInit {
  @ViewChild('actionCol', {static: true}) actionCol: TemplateRef<unknown>;
  @ViewChild('nameCol', {static: true}) nameCol: TemplateRef<unknown>;

  @Input() title: Observable<string>;
  @Input() message: Observable<string>;
  @Input() itemId: any;
  @Input() service: DeleteEntityServiceInterface;
  @Input() entityType: PtEntity;

  public rowData: ItemDocDependency[] = [];
  public columnDefs: ColDef [] = [];
  public gridOptions: GridOptions;
  public IconOpen = IoniconsOutline.open;
  private agGridLangMap: AgGridLangMap;
  private readonly env;

  constructor(
    @Inject(WidgetConfig) private config,
    private store: Store,
    private translateService: TranslateService,
    public activeModal: NgbActiveModal) {
    this.agGridLangMap = new AgGridLangMap(this.translateService.currentLang);
    this.env = this.config.env;
  }

  ngOnInit(): void {
    this.service.getDocDependencies(this.itemId).subscribe(
      (success: ItemDocDependency[]) => {
        this.columnDefs = this.createColumnDefs();
        this.gridOptions = this.createGridOptions();

        this.rowData = success;
      }
    );
  }

  // eslint-disable-next-line
  getResourceType(row: any): WorkspaceItemType {
    return DocumentItemUtils.getResourceType(row.docType);
  }

  selectItem(row: ItemDocDependency) {
    const resourceSubType = DocumentItemUtils.getResourceType(row.docType);
    switch (resourceSubType) {
      case WorkspaceItemType.APP_ASSESSMENT:
        this.navigateToBPM(PtUiAppRoutes.APP_ASSESSMENTS, row.docId);
        break;
      case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
        this.navigateToBPM(PtUiAppRoutes.GLOBAL_MEASURE_CHECK, row.docId);
        break;
      case WorkspaceItemType.AI_ASSESSMENT:
        this.navigateToBPM(PtUiAppRoutes.AI_ASSESSMENTS, row.docId);
        break;
      case WorkspaceItemType.COVID_ASSESSMENT:
        this.navigateToBPM(PtUiAppRoutes.COVID_ASSESSMENTS, row.docId);
        break;
      case WorkspaceItemType.TREATMENT_HOLDER:
        this.navigateToBPM(PtUiAppRoutes.TREATMENTS_HOLDER, row.docId);
        break;
      case WorkspaceItemType.TREATMENT_RESPONSIBLE:
        this.navigateToBPM(PtUiAppRoutes.TREATMENTS_RESPONSIBLE, row.docId);
        break;
      case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
        this.navigateToBPM(PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE, row.docId);
        break;
      case WorkspaceItemType.TREATMENT_PROJECT:
        this.navigateToBPM(PtUiAppRoutes.PROJECT_CARDS, row.docId);
        break;
      default:
    }

  }

  navigateToBPM(entityPath: string, entityId: number) {
    this.store.dispatch(PtCoreActions.navigateTo({
      route: [
        PtUiAppRoutes.BUSINESS_MANAGEMENT,
        entityPath
      ],
      queryParams: {
        opened: true,
        id: entityId
      },
      onBlankPage: true
    }));
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: 'name',
        headerName: this.agGridLangMap.getOf('name'),
        flex: 1,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {ngTemplate: this.nameCol}
      },
      {
        flex: 0,
        width: 58,
        headerName: '',
        field: 'action',
        resizable: true,
        cellRenderer: CustomRendererComponent,
        cellRendererParams: {ngTemplate: this.actionCol}
      }
    ];
  }

  private createGridOptions(): GridOptions {
    return {
      ...this.env.defaultGridOptions
    } as GridOptions;
  }
}

